import { isMeldKindVisible } from '@core/meld-visibility';
import { PlayerActionType, type TPlayerAction, type TPlayerActionType } from '@core/actions';
import { canHu } from '@core/hu';
import { removeFromConcealed } from '@core/hand';
import { type TMeldType } from '@core/meld';
import { RoundPhase } from '@core/round';
import type { IRoundDriver } from '@core/round-driver';
import { findKongAddedOption, findKongConcealedOptions } from '@core/ruleset';
import { isTenpai } from '@core/tenpai';
import { tileToIndex } from '@core/tile';
import type { TSeat, TWind } from '@core/types';
import { liveWallRemaining } from '@core/wall';

export interface IAIPublicMeld {
  readonly type: TMeldType;
  readonly kinds: readonly number[]; // 他家暗槓為空陣列，只公開組數。
}
export interface IAICandidate {
  readonly type: TPlayerActionType;
  readonly kind: number | null;
  readonly removed: readonly number[];
}
export interface IAIPublicContext {
  readonly base: number;
  readonly taiUnit: number;
  readonly balances: readonly number[];
}
export interface IAIObservation extends IAIPublicContext {
  readonly seat: TSeat;
  readonly phase: string;
  readonly concealed: readonly number[];
  readonly drawn: number | null;
  readonly melds: readonly (readonly IAIPublicMeld[])[];
  readonly flowers: readonly (readonly number[])[];
  readonly discards: readonly (readonly number[])[];
  readonly declared: readonly boolean[];
  readonly wallRemaining: number;
  readonly dealerSeat: TSeat;
  readonly dealerStreak: number;
  readonly prevailingWind: TWind;
  readonly seatWind: TWind;
  readonly incoming: {
    readonly kind: number;
    readonly seat: TSeat;
    readonly addedKong: boolean;
  } | null;
  readonly candidates: readonly IAICandidate[];
}
export interface IAIDecisionFrame {
  readonly observation: IAIObservation;
  readonly actions: readonly TPlayerAction[]; // 僅執行器持有，絕不可送入 Worker。
}

/** 建立決策框架只需要讀狀態與送動作；本機 CRound 與伺服器版牌局都符合。 */
export type TDecisionRound = Pick<IRoundDriver, 'getState' | 'hasDeclaredTenpai' | 'submitAction'>;

export const buildDecisionFrame = (
  round: TDecisionRound,
  seat: TSeat,
  context: IAIPublicContext = {
    base: 100,
    taiUnit: 20,
    balances: [0, 0, 0, 0],
  }
): IAIDecisionFrame | null => {
  const s = round.getState();
  const hand = s.hands[seat];
  const actions: TPlayerAction[] = [];
  if (s.phase === RoundPhase.WaitingReaction) {
    if (!s.pendingReactionSeats.has(seat) || s.receivedReactions.has(seat)) return null;
    const legal = s.pendingReactions.get(seat);
    if (!legal) return null;
    const huWindow = s.pendingHuIndex < s.pendingHuSeats.length;
    if (huWindow && legal.canHu) actions.push({ type: PlayerActionType.Hu });
    if (!huWindow && !s.declaredTenpai.has(seat)) actions.push(...legal.reactions);
    actions.push({ type: PlayerActionType.Pass });
  } else if (s.currentSeat === seat && s.phase === RoundPhase.InitialKong) {
    for (const option of findKongConcealedOptions(hand))
      actions.push({ type: PlayerActionType.KongConcealed, option });
    actions.push({ type: PlayerActionType.Pass });
  } else if (s.currentSeat === seat && s.phase === RoundPhase.Turn) {
    if (s.lastDrawnTile && canHu(hand, s.lastDrawnTile, true))
      actions.push({ type: PlayerActionType.Hu });
    if (s.lastDrawnTile && !s.declaredTenpai.has(seat)) {
      for (const option of findKongConcealedOptions(hand))
        actions.push({ type: PlayerActionType.KongConcealed, option });
      const added = findKongAddedOption(hand, s.lastDrawnTile);
      if (added) actions.push({ type: PlayerActionType.KongAdded, option: added });
    }
    const tiles =
      s.declaredTenpai.has(seat) && s.lastDrawnTile ? [s.lastDrawnTile] : hand.concealed;
    const seen = new Set<number>();
    for (const tile of [...tiles].sort((a, b) => tileToIndex(a) - tileToIndex(b))) {
      const kind = tileToIndex(tile);
      if (!seen.has(kind)) actions.push({ type: PlayerActionType.Discard, tile });
      seen.add(kind);
    }
  } else return null;
  const candidates = actions.map((action): IAICandidate => {
    if (action.type === PlayerActionType.Discard)
      return {
        type: action.type,
        kind: tileToIndex(action.tile),
        removed: [tileToIndex(action.tile)],
      };
    if (action.type === PlayerActionType.KongAdded)
      return {
        type: action.type,
        kind: tileToIndex(action.option.drawnTile),
        removed: [tileToIndex(action.option.drawnTile)],
      };
    if ('option' in action)
      return {
        type: action.type,
        kind: null,
        removed: action.option.handTiles.map(tileToIndex).sort((a, b) => a - b),
      };
    return { type: action.type, kind: null, removed: [] };
  });
  const incoming = s.pendingAddedKong
    ? {
        kind: tileToIndex(s.pendingAddedKong.option.drawnTile),
        seat: s.pendingAddedKong.seat,
        addedKong: true,
      }
    : s.pendingDiscard
      ? {
          kind: tileToIndex(s.pendingDiscard.tile),
          seat: s.pendingDiscard.seat,
          addedKong: false,
        }
      : null;
  return {
    actions,
    observation: {
      seat,
      phase: s.phase,
      base: context.base,
      taiUnit: context.taiUnit,
      balances: [...context.balances],
      concealed: hand.concealed.map(tileToIndex).sort((a, b) => a - b),
      drawn:
        s.phase === RoundPhase.Turn && s.currentSeat === seat && s.lastDrawnTile
          ? tileToIndex(s.lastDrawnTile)
          : null,
      melds: s.hands.map((h, i) =>
        h.melds.map((m) => ({
          type: m.type,
          kinds: isMeldKindVisible(m.type, i === seat) ? m.tiles.map(tileToIndex) : [],
        }))
      ),
      flowers: s.hands.map((h) => h.flowers.map(tileToIndex)),
      discards: s.discards.map((river) => river.map(tileToIndex)),
      declared: [0, 1, 2, 3].map((i) => s.declaredTenpai.has(i as TSeat)),
      wallRemaining: liveWallRemaining(s.wall),
      dealerSeat: s.config.dealerSeat,
      dealerStreak: s.config.dealerStreak ?? 0,
      prevailingWind: s.config.prevailingWind,
      seatWind: s.config.seatWinds[seat],
      incoming,
      candidates,
    },
  };
};

/** 未知張數包含對手暗牌與暗槓，並非實際可摸牌數。 */
export const unknownKindCounts = (o: IAIObservation): number[] => {
  const counts = Array<number>(34).fill(4);
  const remove = (kind: number): void => {
    if (kind < 34) counts[kind] = Math.max(0, counts[kind]! - 1);
  };
  o.concealed.forEach(remove);
  o.discards.forEach((river) => river.forEach(remove));
  o.melds.forEach((melds) => melds.forEach((meld) => meld.kinds.forEach(remove)));
  // 加槓宣告時，第四張已公開，尚未併入碰牌；一般捨牌已在河中。
  if (o.incoming?.addedKong) remove(o.incoming.kind);
  return counts;
};

/** 決策候選 → 實際送進牌局的動作（出牌後成聽時一併宣告聽牌）。 */
export const decisionAction = (
  round: TDecisionRound,
  seat: TSeat,
  frame: IAIDecisionFrame,
  index: number
): TPlayerAction => {
  if (!Number.isInteger(index) || !frame.actions[index]) throw new Error('Invalid AI candidate');
  const action = frame.actions[index]!;
  return action.type === PlayerActionType.Discard && !round.hasDeclaredTenpai(seat)
    ? {
        ...action,
        declareTenpai: isTenpai(removeFromConcealed(round.getState().hands[seat], action.tile)),
      }
    : action;
};

export const submitDecision = (
  round: TDecisionRound,
  seat: TSeat,
  frame: IAIDecisionFrame,
  index: number
): void => {
  round.submitAction(seat, decisionAction(round, seat, frame, index));
};
